import type { SVGProps } from "react";
import {
  ArrowDownOnSquareStackIcon,
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowLeftOnRectangleIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowRightOnRectangleIcon,
  ArrowTopRightOnSquareIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  ArrowUpTrayIcon,
  ArrowsUpDownIcon,
  Bars3BottomLeftIcon,
  Bars3Icon,
  BellIcon,
  BookOpenIcon,
  BoltIcon,
  CalendarIcon,
  ChartBarIcon,
  ChartPieIcon,
  ChatBubbleLeftRightIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ClockIcon,
  CloudArrowUpIcon,
  ClipboardDocumentIcon,
  Cog6ToothIcon,
  EllipsisHorizontalIcon,
  EllipsisVerticalIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  FlagIcon,
  FunnelIcon,
  GlobeAltIcon,
  HeartIcon,
  HomeIcon,
  InboxIcon,
  LockClosedIcon,
  LockOpenIcon,
  LanguageIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  MicrophoneIcon,
  MoonIcon,
  PauseIcon,
  PencilSquareIcon,
  PhotoIcon,
  PlayIcon,
  PlusIcon,
  ShieldCheckIcon,
  SignalSlashIcon,
  SpeakerWaveIcon,
  Squares2X2Icon,
  StarIcon,
  StopIcon,
  SunIcon,
  TrashIcon,
  TrophyIcon,
  UserCircleIcon,
  UserGroupIcon,
  UserMinusIcon,
  WalletIcon,
  ViewfinderCircleIcon,
  XCircleIcon,
  XMarkIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { Keyboard, PhoneIcon, SendIcon, Maximize2, Minimize2 } from "lucide-react";
import { send } from "process";

export interface IconProps extends SVGProps<SVGSVGElement> {
  name: IconName;
}

const iconMap = {
  activity: BoltIcon,
  alertCircle: ExclamationCircleIcon,
  alertTriangle: ExclamationTriangleIcon,
  arrowLeft: ArrowLeftIcon,
  arrowRight: ArrowRightIcon,
  arrowUpDown: ArrowsUpDownIcon,
  barChart3: ChartBarIcon,
  pieChart: ChartPieIcon,
  bookOpen: BookOpenIcon,
  calendar: CalendarIcon,
  check: CheckIcon,
  checkCircle: CheckCircleIcon,
  checkCircle2: CheckBadgeIcon,
  chevronLeft: ChevronLeftIcon,
  chevronRight: ChevronRightIcon,
  chevronDown: ChevronDownIcon,
  chevronUp: ChevronUpIcon,
  compass: GlobeAltIcon,
  download: ArrowDownTrayIcon,
  externalLink: ArrowTopRightOnSquareIcon,
  eye: EyeIcon,
  eyeOff: EyeSlashIcon,
  filter: FunnelIcon,
  fileText: DocumentTextIcon,
  flag: FlagIcon,
  globe: GlobeAltIcon,
  bell: BellIcon,
  heart: HeartIcon,
  home: HomeIcon,
  inbox: InboxIcon,
  image: PhotoIcon,
  keyboard: Keyboard,
  lock: LockClosedIcon,
  lockOpen: LockOpenIcon,
  languages: LanguageIcon,
  logIn: ArrowRightOnRectangleIcon,
  logOut: ArrowLeftOnRectangleIcon,
  mail: EnvelopeIcon,
  mapPin: MapPinIcon,
  menu: Bars3Icon,
  moreHorizontal: EllipsisHorizontalIcon,
  messageSquare: ChatBubbleLeftRightIcon,
  mic: MicrophoneIcon,
  moon: MoonIcon,
  mountain: ViewfinderCircleIcon,
  moreVertical: EllipsisVerticalIcon,
  loader2: ArrowPathIcon,
  layoutDashboard: Squares2X2Icon,
  pause: PauseIcon,
  play: PlayIcon,
  plus: PlusIcon,
  panelLeft: Bars3BottomLeftIcon,
  refreshCw: ArrowPathIcon,
  save: ArrowDownOnSquareStackIcon,
  edit: PencilSquareIcon,
  search: MagnifyingGlassIcon,
  settings: Cog6ToothIcon,
  shield: ShieldCheckIcon,
  square: StopIcon,
  trash2: TrashIcon,
  star: StarIcon,
  sun: SunIcon,
  target: TrophyIcon,
  trendingDown: ArrowTrendingDownIcon,
  trendingUp: ArrowTrendingUpIcon,
  upload: ArrowUpTrayIcon,
  user: UserCircleIcon,
  userCheck: ShieldCheckIcon,
  userX: UserMinusIcon,
  users: UserGroupIcon,
  wallet: WalletIcon,
  wifiOff: SignalSlashIcon,
  clock: ClockIcon,
  cloudUpload: CloudArrowUpIcon,
  copy: ClipboardDocumentIcon,
  volume2: SpeakerWaveIcon,
  x: XMarkIcon,
  xCircle: XCircleIcon,
  zap: BoltIcon,
  phone: PhoneIcon,
  history: ClockIcon,
  info: InformationCircleIcon,
  send: SendIcon,
  maximize2: Maximize2,
  minimize2: Minimize2,
} as const;

export type IconName = keyof typeof iconMap;

export function Icon({ name, ...props }: IconProps) {
  const HeroIcon = iconMap[name];
  if (!HeroIcon) {
    return null;
  }
  return <HeroIcon strokeWidth={1.6} {...props} />;
}
